import type { LogsInitConfiguration } from '@datadog/browser-logs';
import type { RumInitConfiguration } from '@datadog/browser-rum';

import { RuntimeEnvironmentOptions, getEnvFromHostname } from '@growtherapy/analytics';

function withGraphQlTracking(
  urls: string[],
  trackPayload: boolean = true,
): NonNullable<RumInitConfiguration['allowedGraphQlUrls']> {
  return urls.map((match) => ({ match, trackPayload, trackResponseErrors: true }));
}

export function getDataDogConfig(): (LogsInitConfiguration & RumInitConfiguration) | null {
  const env = getEnvFromHostname({ hostname: window.location.hostname });
  const base = {
    applicationId: '7739d4d0-5448-43a0-a45b-1698285d55aa',
    clientToken: 'pub13ed140a1e6ea7012731bc59e90b550e',
    enableExperimentalFeatures: ['feature_flags'],
    site: 'datadoghq.com',
    service: 'telehealth.spa',
    version: import.meta.env.VITE_VERSION,
  } satisfies Partial<RumInitConfiguration>;

  switch (env) {
    case RuntimeEnvironmentOptions.production: {
      const prodTracingUrls = ['https://telehealth.growtherapy.com/api', 'https://provider.growtherapy.com/graphql'];
      return {
        ...base,
        env: 'production',
        allowedTracingUrls: prodTracingUrls,
        allowedGraphQlUrls: withGraphQlTracking(prodTracingUrls, false),
        proxy: 'https://telehealth.growtherapy.com/dd/api',
      };
    }
    case RuntimeEnvironmentOptions.development: {
      const devTracingUrls = [
        `https://provider.${import.meta.env.VITE_DEVELOPMENT_MONOLITH_BASE_URL}/graphql`,
        `https://${import.meta.env.VITE_DEVELOPMENT_TELEHEALTH_BASE_URL}/api`,
      ];
      return {
        ...base,
        env: 'development',
        allowedTracingUrls: devTracingUrls,
        allowedGraphQlUrls: withGraphQlTracking(devTracingUrls),
      };
    }
    case RuntimeEnvironmentOptions.staging: {
      const stagingTracingUrls = [
        'https://telehealth.growtherapystaging.com/api',
        'https://provider.growtherapystaging.com/graphql',
      ];
      return {
        ...base,
        env: 'staging',
        allowedTracingUrls: stagingTracingUrls,
        allowedGraphQlUrls: withGraphQlTracking(stagingTracingUrls),
        proxy: 'https://telehealth.growtherapystaging.com/dd/api',
      };
    }
    default:
      return null;
  }
}
